// SPDX-License-Identifier: MIT
// Table Sync Service - Syncs Kepler.gl datasets to DuckDB tables
// Adapted from Kepler.gl patterns for Atlas Viewer

import { DuckDBService } from './duckdb-service';
import { generateUniqueTableName } from '../utils/duckdb-utils';
import { tableFromArrays, type Table as ArrowTable } from 'apache-arrow';
import type { TableSchema, SqlColumn } from '../types';

/**
 * Kepler.gl dataset field structure
 */
interface KeplerField {
  name: string;
  type: string;
  format?: string;
  analyzerType?: string;
}

/**
 * Kepler.gl dataset structure
 */
interface KeplerDataset {
  id: string;
  label: string;
  fields: KeplerField[];
  dataContainer?: {
    numRows: () => number;
    getColumn: (name: string) => unknown[];
    getTable?: () => ArrowTable | null; // Returns underlying Arrow table (Kepler.gl 3.x+)
    valueAt?: (rowIdx: number, colIdx: number) => unknown; // Access individual cell values
  };
  allData?: unknown[][];
  color?: number[];
}

/**
 * Kepler.gl visState structure for dataset access
 */
interface KeplerVisState {
  datasets: Record<string, KeplerDataset>;
}

/**
 * Map of Kepler field types to DuckDB types
 */
const KEPLER_TO_DUCKDB_TYPE: Record<string, string> = {
  // Numeric types
  integer: 'INTEGER',
  int: 'INTEGER',
  real: 'DOUBLE',
  float: 'DOUBLE',
  // String types
  string: 'VARCHAR',
  // Boolean
  boolean: 'BOOLEAN',
  // Date/time
  date: 'DATE',
  timestamp: 'TIMESTAMP',
  // Geometry types
  geojson: 'GEOMETRY',
  geometry: 'GEOMETRY',
  geopoint: 'GEOMETRY',
  // Default
  object: 'VARCHAR',
};

/**
 * Service for synchronizing Kepler.gl datasets to DuckDB tables
 */
export class TableSyncService {
  private syncedTables: Map<string, { datasetId: string; isSecure: boolean }> = new Map();

  /**
   * Remove tables that belong to datasets that are no longer present
   */
  private async removeTablesMissingFrom(
    currentDatasetIds: Set<string>,
    existingNames: Set<string>
  ): Promise<void> {
    const tablesToRemove = Array.from(this.syncedTables.entries())
      .filter(([, info]) => !currentDatasetIds.has(info.datasetId))
      .map(([tableName]) => tableName);

    for (const tableName of tablesToRemove) {
      existingNames.delete(tableName);
      await this.removeTable(tableName);
    }
  }

  /**
   * Remove all tables tied to a dataset before recreating them
   */
  private async removeTablesForDataset(
    datasetId: string,
    existingNames?: Set<string>
  ): Promise<void> {
    const tables = Array.from(this.syncedTables.entries())
      .filter(([, info]) => info.datasetId === datasetId)
      .map(([tableName]) => tableName);

    for (const tableName of tables) {
      existingNames?.delete(tableName);
      await this.removeTable(tableName);
    }
  }

  /**
   * Sync all datasets from Kepler.gl to DuckDB
   */
  async syncAllDatasets(
    visState: KeplerVisState,
    isSecureSource: (datasetId: string) => boolean
  ): Promise<TableSchema[]> {
    const db = await DuckDBService.getInstance();
    const schemas: TableSchema[] = [];
    const existingNames = new Set<string>();

    // Get existing table names to avoid collisions
    const existingTables = await db.getRegisteredTables();
    existingTables.forEach(name => existingNames.add(name));
    const currentDatasetIds = new Set(Object.keys(visState.datasets));
    await this.removeTablesMissingFrom(currentDatasetIds, existingNames);

    for (const [datasetId, dataset] of Object.entries(visState.datasets)) {
      try {
        await this.removeTablesForDataset(datasetId, existingNames);

        const tableName = generateUniqueTableName(dataset.label || datasetId, existingNames);
        existingNames.add(tableName);

        const isSecure = isSecureSource(datasetId);
        const schema = await this.syncDataset(dataset, tableName, isSecure);

        if (schema) {
          schemas.push(schema);
          this.syncedTables.set(tableName, { datasetId, isSecure });
        }
      } catch (error) {
        console.error(`[TableSync] Failed to sync dataset ${datasetId}:`, error);
      }
    }

    return schemas;
  }

  /**
   * Sync a single dataset to a DuckDB table
   * Uses Arrow path when available (fast), falls back to JSON (slow)
   *
   * Path priority:
   * 1. Direct Arrow table (Parquet files) - fastest
   * 2. Build Arrow from dataContainer (CSV files) - fast
   * 3. Build Arrow from allData (legacy) - fast
   * 4. JSON INSERT (fallback) - slow
   */
  async syncDataset(
    dataset: KeplerDataset,
    tableName: string,
    isSecure: boolean = false
  ): Promise<TableSchema | null> {
    const db = await DuckDBService.getInstance();
    const fields = dataset.fields || [];

    // Helper to cleanup failed table creation
    const cleanupTable = async () => {
      try {
        await db.executeQuery(`DROP TABLE IF EXISTS "${tableName}"`);
      } catch (_err) {
        // Ignore cleanup errors
      }
    };

    // Path 1: Direct Arrow table (Parquet files with ArrowDataContainer)
    if (dataset.dataContainer?.getTable) {
      try {
        const arrowTable = dataset.dataContainer.getTable();
        if (arrowTable && arrowTable.numRows > 0) {
          await cleanupTable(); // Ensure clean state
          const rowCount = await db.insertArrowTableDirect(
            tableName,
            arrowTable,
            dataset.id,
            isSecure
          );
          const columns = await this.getTableColumns(tableName);

          console.log(`[TableSync] Used direct Arrow path for ${tableName} (${rowCount} rows)`);

          return {
            tableName,
            datasetId: dataset.id,
            columns,
            rowCount,
            isSecure,
          };
        }
      } catch (error) {
        await cleanupTable(); // Cleanup on failure
        console.warn(`[TableSync] Direct Arrow path failed for ${dataset.id}:`, error);
      }
    }

    // Path 2: Build Arrow from dataContainer (CSV files with RowDataContainer)
    if (dataset.dataContainer && fields.length > 0) {
      try {
        await cleanupTable(); // Ensure clean state before retry
        const arrowTable = this.buildArrowTableFromDataContainer(dataset.dataContainer, fields);
        if (arrowTable && arrowTable.numRows > 0) {
          const rowCount = await db.insertArrowTableDirect(
            tableName,
            arrowTable,
            dataset.id,
            isSecure
          );
          const columns = await this.getTableColumns(tableName);

          console.log(
            `[TableSync] Used built Arrow path (dataContainer) for ${tableName} (${rowCount} rows)`
          );

          return {
            tableName,
            datasetId: dataset.id,
            columns,
            rowCount,
            isSecure,
          };
        }
      } catch (error) {
        await cleanupTable(); // Cleanup on failure
        console.warn(
          `[TableSync] Built Arrow path (dataContainer) failed for ${dataset.id}:`,
          error
        );
      }
    }

    // Path 3: Build Arrow from allData (legacy datasets)
    if (dataset.allData && dataset.allData.length > 0 && fields.length > 0) {
      try {
        await cleanupTable(); // Ensure clean state before retry
        const arrowTable = this.buildArrowTableFromAllData(dataset.allData, fields);
        if (arrowTable && arrowTable.numRows > 0) {
          const rowCount = await db.insertArrowTableDirect(
            tableName,
            arrowTable,
            dataset.id,
            isSecure
          );
          const columns = await this.getTableColumns(tableName);

          console.log(
            `[TableSync] Used built Arrow path (allData) for ${tableName} (${rowCount} rows)`
          );

          return {
            tableName,
            datasetId: dataset.id,
            columns,
            rowCount,
            isSecure,
          };
        }
      } catch (error) {
        await cleanupTable(); // Cleanup on failure
        console.warn(`[TableSync] Built Arrow path (allData) failed for ${dataset.id}:`, error);
      }
    }

    // Path 4: Fallback to JSON INSERT (slow) - last resort
    const data = this.extractDataFromDataset(dataset);
    if (!data || data.length === 0) {
      console.warn(`[TableSync] Dataset ${dataset.id} has no data`);
      return null;
    }

    // Ensure clean state before JSON path
    await cleanupTable();

    // Register table with DuckDB using JSON (slow path)
    console.warn(
      `[TableSync] Using slow JSON path for ${tableName} - this may take a while for large datasets`
    );
    await db.registerJsonTable(tableName, data, dataset.id, isSecure);
    console.log(`[TableSync] Used JSON path for ${tableName} (${data.length} rows)`);

    // Get the actual schema from DuckDB
    const columns = await this.getTableColumns(tableName);

    return {
      tableName,
      datasetId: dataset.id,
      columns,
      rowCount: data.length,
      isSecure,
    };
  }

  /**
   * Extract data rows from a Kepler.gl dataset
   */
  private extractDataFromDataset(dataset: KeplerDataset): Record<string, unknown>[] {
    const fields = dataset.fields || [];

    // Use allData which contains the raw row data as arrays
    // Each row is an array where values correspond to field indices
    if (dataset.allData && Array.isArray(dataset.allData)) {
      const rows = dataset.allData.map(row => {
        const record: Record<string, unknown> = {};
        fields.forEach((field, idx) => {
          record[field.name] = row[idx] ?? null;
        });
        return record;
      });

      return rows;
    }

    // Try dataContainer as fallback (newer Kepler.gl versions)
    // Note: dataContainer may have different API depending on version
    if (dataset.dataContainer) {
      const numRows = dataset.dataContainer.numRows();
      const rows: Record<string, unknown>[] = [];

      // Try to get data row by row using rowAsArray or similar methods
      for (let i = 0; i < numRows; i++) {
        const row: Record<string, unknown> = {};

        // Try to access each field's value
        for (let fieldIdx = 0; fieldIdx < fields.length; fieldIdx++) {
          const field = fields[fieldIdx];
          try {
            // Try different accessor patterns that Kepler.gl might use
            const dc = dataset.dataContainer as unknown as Record<string, unknown>;

            // Method 1: valueAt(rowIdx, fieldIdx)
            if (typeof dc.valueAt === 'function') {
              row[field.name] =
                (dc.valueAt as (r: number, f: number) => unknown)(i, fieldIdx) ?? null;
            }
            // Method 2: row(rowIdx) returns array
            else if (typeof dc.row === 'function') {
              const rowData = (dc.row as (r: number) => unknown[])(i);
              row[field.name] = rowData?.[fieldIdx] ?? null;
            }
            // Method 3: Direct field access
            else {
              row[field.name] = null;
            }
          } catch {
            row[field.name] = null;
          }
        }
        rows.push(row);
      }

      return rows;
    }

    return [];
  }

  /**
   * Get column information from a synced DuckDB table
   */
  private async getTableColumns(tableName: string): Promise<SqlColumn[]> {
    const db = await DuckDBService.getInstance();

    try {
      return await db.getTableSchema(tableName);
    } catch (error) {
      console.warn(`[TableSync] Failed to get schema for ${tableName}:`, error);
      return [];
    }
  }

  /**
   * Build an Arrow table from a Kepler.gl dataContainer (for CSV files)
   * This is the fast path for datasets that don't have a native Arrow table
   */
  private buildArrowTableFromDataContainer(
    dataContainer: NonNullable<KeplerDataset['dataContainer']>,
    fields: KeplerField[]
  ): ArrowTable | null {
    const numRows = dataContainer.numRows();
    if (numRows === 0 || fields.length === 0) return null;

    // Check if valueAt is available (required for fast extraction)
    if (typeof dataContainer.valueAt !== 'function') {
      console.warn('[TableSync] dataContainer.valueAt not available, cannot build Arrow table');
      return null;
    }

    console.log(
      `[TableSync] Building Arrow table from dataContainer (${numRows} rows, ${fields.length} columns)`
    );
    const startTime = performance.now();

    try {
      // Build column arrays - pre-allocate for performance
      const columns: Record<string, unknown[]> = {};
      for (const field of fields) {
        columns[field.name] = new Array(numRows);
      }

      // Extract data using valueAt (fastest dataContainer method)
      for (let rowIdx = 0; rowIdx < numRows; rowIdx++) {
        for (let colIdx = 0; colIdx < fields.length; colIdx++) {
          columns[fields[colIdx].name][rowIdx] = dataContainer.valueAt(rowIdx, colIdx);
        }
      }

      // Build Arrow table from column arrays
      const arrowTable = tableFromArrays(columns);
      const buildTime = performance.now() - startTime;
      console.log(`[TableSync] Arrow table built in ${buildTime.toFixed(0)}ms`);

      return arrowTable;
    } catch (error) {
      console.warn('[TableSync] Failed to build Arrow table from dataContainer:', error);
      return null;
    }
  }

  /**
   * Build an Arrow table from allData array (legacy datasets)
   */
  private buildArrowTableFromAllData(
    allData: unknown[][],
    fields: KeplerField[]
  ): ArrowTable | null {
    if (!allData || allData.length === 0 || fields.length === 0) return null;

    console.log(
      `[TableSync] Building Arrow table from allData (${allData.length} rows, ${fields.length} columns)`
    );
    const startTime = performance.now();

    try {
      // Build column arrays from row-based data
      const columns: Record<string, unknown[]> = {};
      for (const field of fields) {
        columns[field.name] = new Array(allData.length);
      }

      // Convert row-based to column-based
      for (let rowIdx = 0; rowIdx < allData.length; rowIdx++) {
        const row = allData[rowIdx];
        for (let colIdx = 0; colIdx < fields.length; colIdx++) {
          columns[fields[colIdx].name][rowIdx] = row[colIdx] ?? null;
        }
      }

      // Build Arrow table from column arrays
      const arrowTable = tableFromArrays(columns);
      const buildTime = performance.now() - startTime;
      console.log(`[TableSync] Arrow table built from allData in ${buildTime.toFixed(0)}ms`);

      return arrowTable;
    } catch (error) {
      console.warn('[TableSync] Failed to build Arrow table from allData:', error);
      return null;
    }
  }

  /**
   * Remove a synced table
   */
  async removeTable(tableName: string): Promise<void> {
    const db = await DuckDBService.getInstance();
    await db.unregisterTable(tableName);
    this.syncedTables.delete(tableName);
  }

  /**
   * Check if a table is from a secure source
   */
  isSecureTable(tableName: string): boolean {
    const info = this.syncedTables.get(tableName);
    return info?.isSecure ?? false;
  }

  /**
   * Get the dataset ID for a synced table
   */
  getDatasetId(tableName: string): string | undefined {
    return this.syncedTables.get(tableName)?.datasetId;
  }

  /**
   * Get all synced table names
   */
  getSyncedTableNames(): string[] {
    return Array.from(this.syncedTables.keys());
  }

  /**
   * Clear all synced tables
   */
  async clearAll(): Promise<void> {
    const tableNames = Array.from(this.syncedTables.keys());
    for (const tableName of tableNames) {
      await this.removeTable(tableName);
    }
  }

  /**
   * Convert Kepler.gl field type to DuckDB type
   */
  static keplerTypeToDuckDB(keplerType: string): string {
    const lower = keplerType.toLowerCase();
    return KEPLER_TO_DUCKDB_TYPE[lower] || 'VARCHAR';
  }
}

// Singleton instance
let tableSyncInstance: TableSyncService | null = null;

/**
 * Get the singleton TableSyncService instance
 */
export function getTableSyncService(): TableSyncService {
  if (!tableSyncInstance) {
    tableSyncInstance = new TableSyncService();
  }
  return tableSyncInstance;
}

/**
 * Reset the TableSyncService (for testing)
 */
export async function resetTableSyncService(): Promise<void> {
  if (tableSyncInstance) {
    await tableSyncInstance.clearAll();
    tableSyncInstance = null;
  }
}
